import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {Action} from '@ngrx/store';
import {Actions, Effect} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {Router} from '@angular/router';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {RequestService} from '../services/request.service';
import {
  PostOptions,
  PutOptions,
  DeleteOptions,
} from '../services/interfaces/request.interfaces';
import * as moment from 'moment-timezone';
import {
  startOfDay,
  endOfDay,
  subDays,
  addDays,
  endOfMonth,
  isSameDay,
  isSameMonth,
  setHours,
  addMinutes,
  setMinutes,
  getMinutes,
  subMinutes,
  addHours,
  getHours,
  subHours,
  isEqual,
  differenceInMinutes,
} from 'date-fns';
import {of} from 'rxjs/observable/of';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import {MatDialog} from '@angular/material';

import {ModalComponent} from '../components/popups/modal/modal.component';
import {ErrorComponent} from '../components/popups/error/error.component';

import {State} from '../reducers/index';
import {
  PostCredentialsRequest,
  PostCredentialsResponse,
  PutCredentialsRequest,
  PutCredentialsResponse,
  DeleteCredentialsResponse,
} from './interfaces/appointment.interfaces';
import {
  AppointmentAction,
  APPOINTMENT_REQUEST,
  APPOINTMENT_REQUEST_SUCCESS,
  APPOINTMENT_REQUEST_FAILURE,
} from '../actions/appointment.action';
import {OVERLAY_FINISH} from '../actions/header.action';
import {INFO_UPDATE} from '../actions/info.action';

// Effect service for appointment to make side-effect request
@Injectable()
export class AppointmentEffect {
  private options: PostOptions<PostCredentialsRequest, PostCredentialsResponse>;
  private putOptions: PutOptions<PutCredentialsRequest, PutCredentialsResponse>;
  private deleteOptions: DeleteOptions<DeleteCredentialsResponse>;
  private confirmation: boolean = false;
  private time: any = {
    timeout: null,
    permission: false,
  };

  constructor(
    private dialog: MatDialog,
    private requestService: RequestService,
    private router: Router,
    private actions$: Actions,
    private store: Store<State>
  ) {
    // request configurations
    this.options = {
      url: '/online-booking/appointments/new',
      body: {
        practitioner: null,
        treatment: null,
        priceList: null,
        start: null,
        end: null,
        status: 'onlineBooked',
        newPatient: false,
        allDay: null,
        blocker: null,
        repeat: null,
        occurrences: null,
      },
      handlers: {
        success: this.success.bind(this),
        error: this.error.bind(this),
      },
    };
    this.putOptions = {
      url: '/online-booking/appointments/new',
      body: {
        practitioner: null,
        treatment: null,
        priceList: null,
        start: null,
        end: null,
        status: 'onlineBooked',
        newPatient: false,
        allDay: null,
        blocker: null,
        repeat: null,
        occurrences: null,
      },
      handlers: {
        success: this.success.bind(this),
        error: this.error.bind(this),
      },
    };
    this.deleteOptions = {
      url: '/online-booking/appointments/',
      handlers: {
        success: this.successForDelete.bind(this),
        error: this.error.bind(this),
      },
    };
  }

  public success(response: PostCredentialsResponse): void {
    this.store.dispatch({type: OVERLAY_FINISH});
    // clean data if confirmed
    if (this.confirmation) {
      this.confirmation = false;
      // this.router.navigate([localStorage.getItem('AppId') + '/confirmed']);
      localStorage.removeItem('data');
      localStorage.removeItem('clinicId');
      localStorage.removeItem('treatmentPractitioners');
      localStorage.removeItem('goToTreatment');
      localStorage.removeItem('practitionerId');
      localStorage.removeItem('Appointment');
      localStorage.removeItem('treatmentId');
      localStorage.removeItem('treatmentTime');
      this.store.dispatch({
        type: APPOINTMENT_REQUEST_SUCCESS,
      });
    } else {
      // continue confirmation or registration
      if (
        window.location.hash.split('/')[
        window.location.hash.split('/').length - 1
          ] === 'time'
      ) {
        this.router.navigate([
          localStorage.getItem('AppId') +
          (JSON.parse(localStorage.getItem('currentUser'))
            ? '/confirmation'
            : '/details'),
        ]);
      }
    }
    // set timeout for deleting appointment
    if (this.time.permission) {
      this.time.permission = false;
      this.time.timeout = setTimeout(
        () => {
          this.store.dispatch({type: APPOINTMENT_REQUEST});
        },
        JSON.parse(localStorage.getItem('settings')).timeRequiredForBooking
          ? JSON.parse(localStorage.getItem('settings'))
          .timeRequiredForBooking * 1000
          : 30 * 1000
      );
    }
    // finish appointment effect for new/update
    if (!this.confirmation) {
      if (response.appointment.practitioner) {
        this.store.dispatch({
          type: INFO_UPDATE,
          payload: {
            practitioner: response.appointment.practitioner,
          },
        });
        this.store.dispatch({
          type: APPOINTMENT_REQUEST_SUCCESS,
          payload: {
            appointment: JSON.stringify(response.appointment),
          },
        });
      } else {
        this.store.dispatch({
          type: APPOINTMENT_REQUEST_SUCCESS,
          payload: {
            appointment: JSON.stringify(response.appointment),
          },
        });
      }
    }
  }

  // success for delete with ability to create new
  public successForDelete(response: DeleteCredentialsResponse): void {
    localStorage.removeItem('Appointment');
    this.router.navigate([localStorage.getItem('AppId') + '/time']);
    this.openDialog({
      status: 'Session has Timed Out',
      message: 'Please start again to book your appointment.',
    });
    this.store.dispatch({type: APPOINTMENT_REQUEST_SUCCESS});
  }

  // error handler
  public error(httpErrorResponse: HttpErrorResponse): void {
    this.store.dispatch({type: OVERLAY_FINISH});
    this.store.dispatch({type: APPOINTMENT_REQUEST_FAILURE});
  }

  // open angular material dialog
  private openDialog(message: any): void {
    const dialogRef = this.dialog.open(ErrorComponent, {
      width: '90%',
      data: message,
    });
  }

  private getTimeZone() {
    const offset = new Date().getTimezoneOffset();
    const o = Math.abs(offset);
    return {
      sing: offset < 0 ? '+' : '-',
      minutes: +('00' + (o % 60)).slice(-2),
      hours: +('00' + Math.floor(o / 60)).slice(-2),
    };
  }

  @Effect()
  public appointment$: Observable<any> = this.actions$
    .ofType(APPOINTMENT_REQUEST)
    .map((action: AppointmentAction) => action)
    .mergeMap((state) => {
      if (state.payload) {
        // update/create new appointment
        const dateValues = {
          start: setMinutes(
            setHours(
              startOfDay(state.payload['date']),
              state.payload['time'].value.split(':')[0]
            ),
            state.payload['time'].value.split(':')[1]
          ),
          end: addMinutes(
            setMinutes(
              setHours(
                startOfDay(state.payload['date']),
                state.payload['time'].value.split(':')[0]
              ),
              +state.payload['time'].value.split(':')[1]
            ),
            state.payload['treatment'].defaultLength
          ),
        };
        if (this.getTimeZone().sing === '+') {
          dateValues.start = addMinutes(
            addHours(dateValues.start, this.getTimeZone().hours),
            this.getTimeZone().minutes
          );
          dateValues.end = addMinutes(
            addHours(dateValues.end, this.getTimeZone().hours),
            this.getTimeZone().minutes
          );
        } else {
          dateValues.start = subMinutes(
            subHours(dateValues.start, this.getTimeZone().hours),
            this.getTimeZone().minutes
          );
          dateValues.end = subMinutes(
            subHours(dateValues.end, this.getTimeZone().hours),
            this.getTimeZone().minutes
          );
        }
        const momentFunc: any = moment;
        dateValues.start = momentFunc(dateValues.start).tz('UTC').format();
        dateValues.end = momentFunc(dateValues.end).tz('UTC').format();
        this.options.body = {
          practitioner: state.payload['practitioner'].id,
          treatment: state.payload['treatment'].id,
          priceList: state.payload['price'].id,
          start: dateValues.start,
          end: dateValues.end,
          status: state.payload['confirmation'] ? 'confirmed' : 'onlineBooked',
          newPatient: false,
          allDay: false,
          blocker: false,
          repeat: false,
          occurrences: 1,
        };
        console.log(this.options.body);
        if (localStorage.getItem('Appointment')) {
          this.options.body.status = state.payload['confirmation']
            ? 'confirmed'
            : 'onlineBooked';
          this.options.body.blocker = false;
          this.confirmation = state.payload['confirmation'];
          this.options.url = `/online-booking/appointments/${
            JSON.parse(localStorage.getItem('Appointment')).id
          }`;
          if (localStorage.getItem('currentUserToken')) {
            this.requestService.put<PutCredentialsRequest,
              PutCredentialsResponse>(this.options);
          } else {
            this.router.navigate([localStorage.getItem('AppId') + '/details']);
          }
        } else {
          this.options.body.status = 'onlineBooked';
          this.options.body.blocker = true;
          this.time = {
            timeout: null,
            permission: true,
          };
          this.options.url = `/online-booking/${localStorage.getItem(
            'clinicId'
          )}/appointments/new`;
          this.requestService.post<PostCredentialsRequest,
            PostCredentialsResponse>(this.options);
        }
      } else {
        // appointment delete request
        clearTimeout(this.time.timeout);
        this.deleteOptions.url = `/online-booking/${localStorage.getItem(
          'clinicId'
        )}/appointments/${
          JSON.parse(localStorage.getItem('Appointment')).eventId
        }`;
        this.requestService.delete<DeleteCredentialsResponse>(
          this.deleteOptions
        );
      }
      return Observable.of({
        type: 'APPOINTMENT_REQUEST_PROCESSING',
      });
    });
}



// WEBPACK FOOTER //
// /Users/dtrubetskoi/Desktop/projects/online-booking-widget/src/app/effects/appointment.effect.ts